import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../../auth/contexts/AuthContext';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceType: 'upload' | 'processing' | 'storage' | 'compute_hours' | 'api';
  message: string;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  resourceType,
  message,
}) => {
  const { t } = useTranslation('pricing');
  const navigate = useNavigate();
  const { subscription, usage } = useAuth();

  if (!isOpen) return null;

  const resourceIcons: Record<string, string> = {
    upload: '📤',
    processing: '⚙️',
    storage: '💾',
    compute_hours: '⏱️',
    api: '🔌'
  };

  const resourceTitles: Record<string, string> = {
    upload: t('upgradeModal.titles.upload', 'Upload Limit Reached'),
    processing: t('upgradeModal.titles.processing', 'Processing Limit Reached'),
    storage: t('upgradeModal.titles.storage', 'Storage Limit Reached'),
    compute_hours: t('upgradeModal.titles.computeHours', 'Compute Hours Exhausted'),
    api: t('upgradeModal.titles.api', 'API Limit Reached')
  };

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          className="bg-card rounded-lg shadow-xl max-w-md w-full p-6 relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Icon and Title */}
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">{resourceIcons[resourceType]}</div>
            <h2 className="text-2xl font-bold text-foreground">
              {resourceTitles[resourceType]}
            </h2>
          </div>

          {/* Message */}
          <div className="mb-6">
            <p className="text-foreground text-center mb-4">
              {message}
            </p>

            {/* Current Plan Info — always show Compute Hours + Storage */}
            <div className="bg-muted rounded-lg p-4">
              <div className="text-sm text-muted-foreground">
                <p className="mb-3">
                  <span className="font-semibold">{t('upgradeModal.currentPlan', 'Current Plan')}:</span>{' '}
                  <span className="text-foreground font-bold">{subscription?.plan_name || 'Free'}</span>
                </p>

                {/* Compute Hours */}
                {(() => {
                  const totalHours = subscription?.total_compute_hours || 0;
                  const usedSeconds = usage?.total_compute_seconds || 0;
                  const usedMinutes = Math.round(usedSeconds / 60);
                  const totalMinutes = totalHours * 60;
                  const remainingMinutes = Math.max(0, totalMinutes - usedMinutes);
                  // Only plans with a positive compute-hours allowance have this budget.
                  if (totalHours <= 0) return null;
                  return (
                    <div className={`mb-3 pb-3 border-b border-border ${resourceType === 'compute_hours' ? 'bg-red-50 dark:bg-red-950/30 -mx-2 px-2 py-1 rounded' : ''}`}>
                      <p className="font-semibold mb-1">{t('quota.computeHours', 'Compute Hours')}</p>
                      <p className="mb-0.5">
                        <span className="text-muted-foreground">{t('upgradeModal.used', 'Used')}:</span>{' '}
                        <span className={resourceType === 'compute_hours' ? 'text-red-600 dark:text-red-400 font-bold' : 'text-foreground'}>
                          {`${usedMinutes}min / ${totalMinutes.toFixed(0)}min`}
                        </span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">{t('upgradeModal.remaining', 'Remaining')}:</span>{' '}
                        <span className="text-foreground">
                          {`${remainingMinutes.toFixed(0)}min`}
                        </span>
                      </p>
                    </div>
                  );
                })()}

                {/* Storage */}
                {(() => {
                  const usedGB = usage?.storage_used_gb || 0;
                  const maxGB = subscription?.max_storage_gb || 0;
                  const remainingGB = Math.max(0, Number(maxGB) - usedGB);
                  return (
                    <div className={resourceType === 'storage' ? 'bg-red-50 dark:bg-red-950/30 -mx-2 px-2 py-1 rounded' : ''}>
                      <p className="font-semibold mb-1">{t('upgradeModal.storageLimit', 'Storage')}</p>
                      <p className="mb-0.5">
                        <span className="text-muted-foreground">{t('upgradeModal.used', 'Used')}:</span>{' '}
                        <span className={resourceType === 'storage' ? 'text-red-600 dark:text-red-400 font-bold' : 'text-foreground'}>
                          {usedGB.toFixed(2)} / {Number(maxGB).toFixed(0)} GB
                        </span>
                      </p>
                      <p>
                        <span className="text-muted-foreground">{t('upgradeModal.remaining', 'Remaining')}:</span>{' '}
                        <span className="text-foreground">{remainingGB.toFixed(2)} GB</span>
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Upgrade Benefits */}
          <div className="mb-6 bg-accent rounded-lg p-4">
            <p className="text-sm font-semibold text-accent-foreground mb-2">
              {t('upgradeModal.benefitsTitle', 'Upgrade to get:')}
            </p>
            <ul className="text-sm text-accent-foreground space-y-1">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>{t('upgradeModal.benefit1', 'Higher upload and processing limits')}</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>{t('upgradeModal.benefit2', 'More storage space')}</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>{t('upgradeModal.benefit3', 'Priority support')}</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleUpgrade}
              className="flex-1 bg-primary text-primary-foreground py-3 px-4 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              {t('upgradeModal.upgradeButton', 'Upgrade Plan')}
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-muted text-muted-foreground py-3 px-4 rounded-lg font-semibold hover:bg-muted/80 transition-colors"
            >
              {t('upgradeModal.cancelButton', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpgradeModal;
